import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { CASE_CATEGORIES } from '../constants/caseCategories';
import PhoneInput from '../components/common/PhoneInput';
import { getErrorKey } from '../services/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_ENABLED = GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID !== 'placeholder-for-google-oauth';

const REGIONS = [
  'tashkent_city', 'tashkent', 'andijan', 'fergana', 'namangan',
  'samarkand', 'bukhara', 'navoi', 'kashkadarya', 'surkhandarya',
  'jizzakh', 'sirdarya', 'khorezm', 'karakalpakstan',
];

// Always store Russian labels in DB for consistent search
const REGION_LABELS_RU = {
  tashkent_city:  'Ташкент (город)',
  tashkent:       'Ташкентская область',
  andijan:        'Андижанская область',
  fergana:        'Ферганская область',
  namangan:       'Наманганская область',
  samarkand:      'Самаркандская область',
  bukhara:        'Бухарская область',
  navoi:          'Навоийская область',
  kashkadarya:    'Кашкадарьинская область',
  surkhandarya:   'Сурхандарьинская область',
  jizzakh:        'Джизакская область',
  sirdarya:       'Сырдарьинская область',
  khorezm:        'Хорезмская область',
  karakalpakstan: 'Республика Каракалпакстан',
};

// Subregions for all regions except tashkent_city — { label: Uzbek, value: Russian (stored in DB) }
const REGION_SUBREGIONS = {
  tashkent: [
    { label: 'Нурафшон шаҳри',      value: 'г. Нурафшон' },
    { label: 'Чирчиқ шаҳри',        value: 'г. Чирчик' },
    { label: 'Олмалиқ шаҳри',       value: 'г. Алмалык' },
    { label: 'Ангрен шаҳри',        value: 'г. Ангрен' },
    { label: 'Бекобод шаҳри',       value: 'г. Бекабад' },
    { label: 'Янгийўл шаҳри',       value: 'г. Янгиюль' },
    { label: 'Зангиота тумани',     value: 'Зангиатинский район' },
    { label: 'Қибрай тумани',       value: 'Кибрайский район' },
    { label: 'Бўстонлиқ тумани',    value: 'Бостанлыкский район' },
    { label: 'Бўка тумани',         value: 'Букинский район' },
    { label: 'Паркент тумани',      value: 'Паркентский район' },
    { label: 'Пскент тумани',       value: 'Пскентский район' },
    { label: 'Тошкент тумани',      value: 'Ташкентский район' },
    { label: 'Ўртачирчиқ тумани',   value: 'Уртачирчикский район' },
    { label: 'Юқоричирчиқ тумани',  value: 'Юкоричирчикский район' },
    { label: 'Оҳангарон шаҳри',     value: 'г. Охангарон' },
    { label: 'Чиноз тумани',        value: 'Чиназский район' },
    { label: 'Оққўрғон тумани',     value: 'Аккурганский район' },
    { label: 'Қуйи Чирчиқ тумани', value: 'Куйичирчикский район' },
  ],
  andijan: [
    { label: 'Андижон шаҳри',        value: 'г. Андижан' },
    { label: 'Андижон тумани',       value: 'Андижанский район' },
    { label: 'Асака тумани',         value: 'Асакинский район' },
    { label: 'Балиқчи тумани',       value: 'Балыкчинский район' },
    { label: 'Булоқбоши тумани',     value: 'Булакбашский район' },
    { label: 'Бўстон тумани',        value: 'Бустанский район' },
    { label: 'Жалақудуқ тумани',     value: 'Джалалкудукский район' },
    { label: 'Избоскан тумани',      value: 'Избасканский район' },
    { label: 'Мархамат тумани',      value: 'Мархаматский район' },
    { label: 'Олтинкўл тумани',      value: 'Алтыкульский район' },
    { label: 'Пахтаобод тумани',     value: 'Пахтаабадский район' },
    { label: 'Қўрғонтепа тумани',    value: 'Кургантепинский район' },
    { label: 'Улуғнор тумани',       value: 'Улугнорский район' },
    { label: 'Хонобод тумани',       value: 'Ханабадский район' },
    { label: 'Хўжаобод тумани',      value: 'Ходжаабадский район' },
    { label: 'Шаҳрихон тумани',      value: 'Шахриханский район' },
  ],
  fergana: [
    { label: 'Фарғона шаҳри',       value: 'г. Фергана' },
    { label: 'Қўқон шаҳри',         value: 'г. Коканд' },
    { label: 'Марғилон шаҳри',      value: 'г. Маргилан' },
    { label: 'Қувасой шаҳри',       value: 'г. Кувасай' },
    { label: 'Қува тумани',         value: 'Кувинский район' },
    { label: 'Риштон тумани',       value: 'Риштанский район' },
    { label: 'Бағдод тумани',       value: 'Багдадский район' },
    { label: 'Бешариқ тумани',      value: 'Бешарыкский район' },
    { label: 'Бувайда тумани',       value: 'Бувайдинский район' },
    { label: 'Данғара тумани',       value: 'Дангаринский район' },
    { label: 'Қуштепа тумани',       value: 'Куштепинский район' },
    { label: 'Сўх тумани',           value: 'Сохский район' },
    { label: 'Тошлоқ тумани',        value: 'Ташлакский район' },
    { label: 'Фурқат тумани',        value: 'Фуркатский район' },
    { label: 'Ёзёвон тумани',        value: 'Язяванский район' },
  ],
  namangan: [
    { label: 'Наманган шаҳри',        value: 'г. Наманган' },
    { label: 'Чуст тумани',          value: 'Чустский район' },
    { label: 'Поп тумани',           value: 'Папский район' },
    { label: 'Косонсой тумани',      value: 'Касансайский район' },
    { label: 'Давлатобод тумани',    value: 'Давлатабадский район' },
    { label: 'Мингбулоқ тумани',     value: 'Мингбулакский район' },
    { label: 'Норин тумани',         value: 'Норинский район' },
    { label: 'Тўрақўрғон тумани',    value: 'Туракурганский район' },
    { label: 'Уйчи тумани',          value: 'Уйчинский район' },
    { label: 'Учқўрғон тумани',      value: 'Учкурганский район' },
    { label: 'Янгиқўрғон тумани',    value: 'Янгикурганский район' },
    { label: 'Янги Наманган тумани', value: 'Янги-Наманганский район' },
    { label: 'Чортоқ тумани',        value: 'Чартакский район' },
  ],
  samarkand: [
    { label: 'Самарқанд шаҳри',     value: 'г. Самарканд' },
    { label: 'Каттақўрғон шаҳри',   value: 'г. Каттакурган' },
    { label: 'Ургут тумани',        value: 'Ургутский район' },
    { label: 'Пайариқ тумани',      value: 'Пайарыкский район' },
    { label: 'Иштихон тумани',      value: 'Иштиханский район' },
    { label: 'Жомбой тумани',       value: 'Джамбайский район' },
    { label: 'Оқдарё тумани',       value: 'Акдарьинский район' },
    { label: 'Булунғур тумани',     value: 'Булунгурский район' },
    { label: 'Қўшрабод тумани',     value: 'Кошрабадский район' },
    { label: 'Нарпай тумани',       value: 'Нарпайский район' },
    { label: 'Нуробод тумани',      value: 'Нурабадский район' },
    { label: 'Пастдарғом тумани',   value: 'Пастдаргомский район' },
    { label: 'Пахтачи тумани',      value: 'Пахтачийский район' },
    { label: 'Тайлоқ тумани',       value: 'Тайлакский район' },
  ],
  bukhara: [
    { label: 'Бухоро шаҳри',        value: 'г. Бухара' },
    { label: 'Когон тумани',         value: 'г. Каган' },
    { label: 'Ромитан тумани',      value: 'Ромитанский район' },
    { label: 'Қоракўл тумани',      value: 'Каракульский район' },
    { label: 'Ғиждувон тумани',     value: 'Гиждуванский район' },
    { label: 'Вобкент тумани',      value: 'Вабкентский район' },
    { label: 'Жондор тумани',       value: 'Джандарский район' },
    { label: 'Қоровулбозор тумани', value: 'Каравулбазарский район' },
    { label: 'Олот тумани',         value: 'Алатский район' },
    { label: 'Пешку тумани',        value: 'Пешкунский район' },
    { label: 'Шофиркон тумани',     value: 'Шафирканский район' },
  ],
  navoi: [
    { label: 'Навоий шаҳри',        value: 'г. Навои' },
    { label: 'Зарафшон шаҳри',      value: 'г. Зарафшан' },
    { label: 'Учқудуқ тумани',      value: 'Учкудукский район' },
    { label: 'Нурота тумани',       value: 'Нуратинский район' },
    { label: 'Ғозғон шаҳри',        value: 'Газганский район' },
    { label: 'Конимех тумани',      value: 'Конимехский район' },
    { label: 'Кармана тумани',      value: 'Карманинский район' },
    { label: 'Қизилтепа тумани',    value: 'Кызылтепинский район' },
    { label: 'Навбаҳор тумани',     value: 'Навбахорский район' },
    { label: 'Томди тумани',        value: 'Томдинский район' },
    { label: 'Хатирчи тумани',      value: 'Хатырчинский район' },
  ],
  kashkadarya: [
    { label: 'Қарши шаҳри',         value: 'г. Карши' },
    { label: 'Шаҳрисабз шаҳри',     value: 'г. Шахрисабз' },
    { label: 'Қарши тумани',        value: 'Каршинский район' },
    { label: 'Китоб тумани',        value: 'Китабский район' },
    { label: 'Кўкдала тумани',      value: 'Кукдалинский район' },
    { label: 'Косон тумани',        value: 'Касанский район' },
    { label: 'Муборак тумани',      value: 'Мубарекский район' },
    { label: 'Миришкор тумани',     value: 'Миришкорский район' },
    { label: 'Ғузор тумани',        value: 'Гузарский район' },
    { label: 'Деҳқонобод тумани',   value: 'Дехканабадский район' },
    { label: 'Қамаши тумани',       value: 'Камашинский район' },
    { label: 'Қасби тумани',        value: 'Касбинский район' },
    { label: 'Нишон тумани',        value: 'Нишанский район' },
    { label: 'Чироқчи тумани',      value: 'Чиракчинский район' },
    { label: 'Яккабоғ тумани',      value: 'Яккабагский район' },
  ],
  surkhandarya: [
    { label: 'Термиз шаҳри',        value: 'г. Термез' },
    { label: 'Ангор тумани',        value: 'Ангорский район' },
    { label: 'Денов тумани',        value: 'Денауский район' },
    { label: 'Шеробод тумани',      value: 'Шерабадский район' },
    { label: 'Бойсун тумани',       value: 'Байсунский район' },
    { label: 'Бандихон тумани',     value: 'Бандиханский район' },
    { label: 'Жарқўрғон тумани',    value: 'Джаркурганский район' },
    { label: 'Қизириқ тумани',      value: 'Кизирикский район' },
    { label: 'Қумқўрғон тумани',    value: 'Кумкурганский район' },
    { label: 'Музработ тумани',     value: 'Музрабадский район' },
    { label: 'Олтинсой тумани',     value: 'Алтынсайский район' },
    { label: 'Сариосиё тумани',     value: 'Сариасийский район' },
    { label: 'Узун тумани',         value: 'Узунский район' },
    { label: 'Шўрчи тумани',        value: 'Шурчинский район' },
  ],
  jizzakh: [
    { label: 'Жиззах шаҳри',        value: 'г. Джизак' },
    { label: 'Зомин тумани',        value: 'Заминский район' },
    { label: 'Ғаллаорол тумани',    value: 'Галляаральский район' },
    { label: 'Янгиобод тумани',     value: 'Янгиабадский район' },
    { label: 'Арнасой тумани',      value: 'Арнасайский район' },
    { label: 'Бахмал тумани',       value: 'Бахмалский район' },
    { label: 'Дўстлик тумани',      value: 'Дустликский район' },
    { label: 'Зарбдор тумани',      value: 'Зарбдарский район' },
    { label: 'Зафаробод тумани',    value: 'Зафарабадский район' },
    { label: 'Мирзачўл тумани',     value: 'Мирзачульский район' },
    { label: 'Пахтакор тумани',     value: 'Пахтакорский район' },
    { label: 'Фориш тумани',        value: 'Фаришский район' },
    { label: 'Шароф Рашидов тумани',value: 'Шараф-Рашидовский район' },
  ],
  sirdarya: [
    { label: 'Гулистон шаҳри',      value: 'г. Гулистан' },
    { label: 'Янгиер шаҳри',        value: 'г. Янгиер' },
    { label: 'Хавос тумани',        value: 'Хавастский район' },
    { label: 'Сардоба тумани',      value: 'Сардобинский район' },
    { label: 'Оқолтин тумани',      value: 'Акалтынский район' },
    { label: 'Боёвут тумани',       value: 'Баяутский район' },
    { label: 'Мирзаобод тумани',    value: 'Мирзаабадский район' },
    { label: 'Сайхунобод тумани',   value: 'Сайхунабадский район' },
    { label: 'Ширин шаҳри',         value: 'г. Ширин' },
    { label: 'Сирдарё тумани',      value: 'Сырдарьинский район' },
  ],
  khorezm: [
    { label: 'Урганч шаҳри',        value: 'г. Ургенч' },
    { label: 'Хива шаҳри',          value: 'г. Хива' },
    { label: 'Питнак шаҳри',        value: 'г. Питнак' },
    { label: 'Хонқа тумани',        value: 'Ханкинский район' },
    { label: 'Шовот тумани',        value: 'Шаватский район' },
    { label: 'Боғот тумани',        value: 'Багатский район' },
    { label: 'Гурлан тумани',       value: 'Гурленский район' },
    { label: 'Қўшкўпир тумани',     value: 'Кушкупырский район' },
    { label: 'Хазорасп тумани',     value: 'Хазараспский район' },
    { label: 'Янгиариқ тумани',     value: 'Янгиарыкский район' },
    { label: 'Янгибозор тумани',    value: 'Янгибазарский район' },
  ],
  karakalpakstan: [
    { label: 'Нукус шаҳри',         value: 'г. Нукус' },
    { label: 'Тахиатош шаҳри',      value: 'г. Тахиаташ' },
    { label: 'Тўрткўл шаҳри',       value: 'г. Турткуль' },
    { label: 'Хўжайли шаҳри',       value: 'г. Ходжейли' },
    { label: 'Чимбой шаҳри',        value: 'г. Чимбай' },
    { label: 'Беруний тумани',      value: 'Берунийский район' },
    { label: 'Қўнғирот тумани',     value: 'Кунградский район' },
    { label: 'Амударё тумани',      value: 'Амударьинский район' },
    { label: 'Кегейли тумани',      value: 'Кегейлийский район' },
    { label: 'Қонликўл тумани',     value: 'Канликольский район' },
    { label: 'Мўйноқ тумани',       value: 'Муйнакский район' },
    { label: 'Тахиатош тумани',     value: 'Тахиатошский район' },
    { label: 'Тахтакўпир тумани',   value: 'Тахтакупырский район' },
    { label: 'Шўманай тумани',      value: 'Шуманайский район' },
    { label: 'Элликқалъа тумани',   value: 'Элликкалинский район' },
  ],
};

const chevronDown = (
  <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.45, flexShrink: 0 }} fill="none">
    <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RegionSelect = ({ value, onChange, inputStyle }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const ref = useRef(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // For display: region labels use t(); subregion labels use label (uz) or value (ru)
  const getSubDisplay = (sub) => language === 'uz' ? sub.label : sub.value;

  const displayLabel = () => {
    if (!value) return null;
    // Check if it's a top-level region Russian label
    const regionKey = Object.entries(REGION_LABELS_RU).find(([, v]) => v === value)?.[0];
    if (regionKey) return t(`regions.${regionKey}`);
    // Otherwise it's a subregion value — find matching subregion to show in correct language
    for (const subs of Object.values(REGION_SUBREGIONS)) {
      const match = subs.find(s => s.value === value);
      if (match) return getSubDisplay(match);
    }
    return value;
  };

  const select = (val) => { onChange(val); setOpen(false); setExpanded(null); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          ...inputStyle,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: 'white',
          textAlign: 'left',
          color: value ? '#2d3748' : '#a0aec0',
        }}
      >
        <span>{displayLabel() || ''}</span>
        {chevronDown}
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
          border: '1px solid #e2e8f0',
          zIndex: 1000,
          maxHeight: '260px',
          overflowY: 'auto',
          padding: '0.4rem 0',
        }}>
          {REGIONS.map(key => {
            const subs = REGION_SUBREGIONS[key];
            const hasSubs = subs && subs.length > 0;
            const isExpanded = expanded === key;

            return (
              <React.Fragment key={key}>
                <RegionOption
                  active={!hasSubs && value === REGION_LABELS_RU[key]}
                  onClick={() => {
                    if (hasSubs) setExpanded(isExpanded ? null : key);
                    else select(REGION_LABELS_RU[key]);
                  }}
                  suffix={hasSubs ? (
                    <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.45, flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} fill="none">
                      <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : null}
                >
                  {t(`regions.${key}`)}
                </RegionOption>
                {hasSubs && isExpanded && subs.map(sub => (
                  <RegionOption
                    key={sub.value}
                    active={value === sub.value}
                    indent
                    onClick={() => select(sub.value)}
                  >
                    {getSubDisplay(sub)}
                  </RegionOption>
                ))}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RegionOption = ({ onClick, active, indent, suffix, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '0.6rem 1rem',
        paddingLeft: indent ? '1.75rem' : '1rem',
        fontSize: indent ? '0.88rem' : '0.95rem',
        cursor: 'pointer',
        color: active ? '#2b6cb0' : indent ? '#4a5568' : '#2d3748',
        fontWeight: active ? '600' : '400',
        backgroundColor: hover ? '#f7fafc' : 'transparent',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <span>{children}</span>
      {suffix}
    </div>
  );
};

const styles = {
  container: {
    minHeight: 'calc(100vh - 180px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backgroundColor: '#f7fafc',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '500px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    boxSizing: 'border-box',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#1a365d',
    textAlign: 'center',
    marginBottom: '2rem',
  },
  userTypeSelect: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  userTypeButton: {
    flex: 1,
    padding: '1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '1rem',
    transition: 'all 0.2s',
  },
  userTypeActive: {
    borderColor: '#3182ce',
    backgroundColor: '#ebf8ff',
    color: '#3182ce',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    fontWeight: '500',
    color: '#4a5568',
    fontSize: '0.9rem',
  },
  input: {
    padding: '0.75rem 1rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  textarea: {
    padding: '0.75rem 1rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
    minHeight: '100px',
    resize: 'none',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  },
  checkboxContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.5rem 0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  checkboxLabelActive: {
    backgroundColor: 'rgba(255,215,0,0.08)',
    borderColor: '#FFD700',
    borderWidth: '1px',
    color: '#0d1f17',
    fontWeight: '600',
  },
  button: {
    backgroundColor: '#FFD700',
    color: '#0d1f17',
    padding: '0.875rem',
    border: 'none',
    borderRadius: '50px',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    margin: '1.5rem 0',
    color: '#a0aec0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e2e8f0',
  },
  googleContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  link: {
    textAlign: 'center',
    marginTop: '1.5rem',
    color: '#718096',
  },
  linkAnchor: {
    color: '#3182ce',
    textDecoration: 'none',
    fontWeight: '500',
  },
  error: {
    backgroundColor: '#fed7d7',
    color: '#c53030',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '0.9rem',
    textAlign: 'center',
    marginBottom: '1rem',
  },
};

const Register = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();

  const prefill = location.state?.prefill || {};
  const [userType, setUserType] = useState(
    location.state?.userType || searchParams.get('userType') || 'client'
  );

  // Sync userType when URL param changes
  useEffect(() => {
    const param = searchParams.get('userType');
    setUserType(param === 'attorney' ? 'attorney' : 'client');
  }, [searchParams]);
  // Best-effort split of a single prefilled "fullName" string (e.g. coming from the guest
  // Request Advice flow) into Фамилия/Исм — Фамилия first, matching Uzbek form convention.
  const prefillNameParts = (prefill.fullName || '').trim().split(/\s+/).filter(Boolean);
  const [formData, setFormData] = useState({
    email: prefill.email || '',
    password: '',
    confirmPassword: '',
    lastName: prefillNameParts[0] || '',
    firstName: prefillNameParts.slice(1).join(' ') || '',
    middleName: '',
    phone: prefill.phone || '',
    location: '',
    barNumber: '',
    licenseDate: '',
    yearsOfExperience: '',
    practiceAreas: [],
    bio: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const [expandedBubble, setExpandedBubble] = useState(null);
  const bubblesRef = useRef(null);
  useEffect(() => {
    const handler = () => setExpandedBubble(null);
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handlePracticeAreaToggle = (area) => {
    const current = formData.practiceAreas;
    if (current.includes(area)) {
      setFormData({ ...formData, practiceAreas: current.filter(a => a !== area) });
    } else {
      setFormData({ ...formData, practiceAreas: [...current, area] });
    }
  };

  const handleBubbleClick = (cat) => {
    if (cat.subs.length === 0) {
      handlePracticeAreaToggle(cat.label);
      return;
    }
    const allSubs = cat.subs;
    const current = formData.practiceAreas;
    const anySelected = allSubs.some(s => current.includes(s));
    if (!anySelected) {
      setFormData({ ...formData, practiceAreas: [...current, ...allSubs.filter(s => !current.includes(s))] });
    } else {
      setFormData({ ...formData, practiceAreas: current.filter(a => !allSubs.includes(a)) });
    }
  };

  const handleDeselectParent = (cat) => {
    const allSubs = cat.subs;
    setFormData({ ...formData, practiceAreas: formData.practiceAreas.filter(a => !allSubs.includes(a)) });
    setExpandedBubble(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.passwordsMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('register.passwordTooShort'));
      return;
    }

    const phoneDigits = formData.phone.replace(/\D/g, '').replace(/^998/, '');
    if (phoneDigits.length < 9) {
      setError(t('register.phoneInvalid'));
      return;
    }

    setLoading(true);

    const fullName = userType === 'attorney'
      ? [formData.lastName, formData.firstName, formData.middleName].filter(Boolean).join(' ')
      : [formData.lastName, formData.firstName].filter(Boolean).join(' ');

    try {
      const submitData = { ...formData, fullName };
      await register({ ...submitData, userType });
      const returnTo = location.state?.returnTo;
      const pendingAdvice = location.state?.prefill?.pendingAdvice;
      if (pendingAdvice && userType === 'client') {
        navigate('/request-advice', {
          state: {
            pendingAdvice,
            prefill: { fullName, phone: formData.phone },
          },
        });
      } else {
        if (!returnTo && userType === 'client') window.location.href = '/';
        else navigate(returnTo || '/attorney/dashboard');
      }
    } catch (err) {
      setError(t(`errors.${getErrorKey(err) || 'registrationFailed'}`));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      const result = await googleLogin(credentialResponse.credential, userType);
      navigate(result.user.userType === 'client' ? '/client/dashboard' : '/attorney/dashboard');
    } catch (err) {
      setError(t(`errors.${getErrorKey(err) || 'googleLoginFailed'}`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>{t('auth.registerTitle')}</h1>

        {userType === 'attorney' && (
          <p style={{ textAlign: 'center', fontSize: '0.95rem', color: '#718096', marginBottom: '1rem', marginTop: '-0.75rem', fontWeight: '500' }}>
            {t('register.forAttorneys')}
          </p>
        )}

        {userType === 'client' && (
          <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#718096', marginBottom: '1.75rem', marginTop: '-1rem' }}>
            <a href="/register?userType=attorney" style={{ color: '#3182ce', textDecoration: 'none', fontWeight: '500' }}>
              {t('register.attorneySignUpLink')}
            </a>
          </p>
        )}

        {error && <div style={styles.error}>{error}</div>}

        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('register.emailRequired')}</label>
            <input type="email" name="email" style={styles.input} value={formData.email} onChange={handleChange} required />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('register.passwordRequired')}</label>
            <input type="password" name="password" style={styles.input} value={formData.password} onChange={handleChange} required />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('register.confirmPasswordRequired')}</label>
            <input type="password" name="confirmPassword" style={styles.input} value={formData.confirmPassword} onChange={handleChange} required />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('register.lastName')}</label>
            <input type="text" name="lastName" style={styles.input} value={formData.lastName} onChange={handleChange} required />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('register.firstName')}</label>
            <input type="text" name="firstName" style={styles.input} value={formData.firstName} onChange={handleChange} required />
          </div>
          {userType === 'attorney' && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>{t('register.middleName')}</label>
              <input type="text" name="middleName" style={styles.input} value={formData.middleName} onChange={handleChange} required />
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('register.phone')} *</label>
            <PhoneInput value={formData.phone} onChange={val => setFormData(p => ({ ...p, phone: val }))} style={styles.input} required />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('register.location')}{userType === 'attorney' && ' *'}</label>
            {userType === 'attorney' ? (
              <RegionSelect
                value={formData.location}
                onChange={(val) => setFormData(prev => ({ ...prev, location: val }))}
                inputStyle={styles.input}
              />
            ) : (
              <input type="text" name="location" style={styles.input} value={formData.location} onChange={handleChange} />
            )}
          </div>

          {userType === 'attorney' && (
            <>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('register.barNumberRequired')}</label>
                <input type="text" name="barNumber" style={styles.input} value={formData.barNumber} onChange={handleChange} required />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('register.licenseDate')} *</label>
                <input
                  type="date"
                  name="licenseDate"
                  style={{ ...styles.input, color: formData.licenseDate ? '#2d3748' : 'transparent' }}
                  value={formData.licenseDate}
                  onChange={handleChange}
                  required
                  max={new Date().toISOString().split('T')[0]}
                  onClick={e => { try { e.target.showPicker(); } catch (_) {} }}
                  onFocus={e => { try { e.target.showPicker(); } catch (_) {} }}
                  onKeyDown={e => e.preventDefault()}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('register.yearsOfExperience')} *</label>
                <input
                  type="number"
                  name="yearsOfExperience"
                  style={styles.input}
                  value={formData.yearsOfExperience}
                  onChange={handleChange}
                  required
                  min="0"
                  max="70"
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('register.practiceAreas')}</label>
                <div ref={bubblesRef} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                  {CASE_CATEGORIES.map((cat, ci) => {
                    if (cat.subs.length === 0) {
                      // "Бошқа турдаги ишлар" — simple checkbox point
                      const checked = formData.practiceAreas.includes(cat.label);
                      return (
                        <label key={ci} onClick={() => handlePracticeAreaToggle(cat.label)} style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          cursor: 'pointer', fontSize: '0.9rem', color: '#4a5568',
                          padding: '0.4rem 0', width: 'auto',
                        }}>
                          <span style={{
                            width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                            border: `1.5px solid ${checked ? '#FFD700' : '#cbd5e0'}`,
                            backgroundColor: checked ? '#FFD700' : 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#0d1f17" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </span>
                          {cat.label}
                        </label>
                      );
                    }
                    const anySelected = cat.subs.some(s => formData.practiceAreas.includes(s));
                    const allSelected = cat.subs.every(s => formData.practiceAreas.includes(s));
                    const isOpen = expandedBubble === ci;
                    return (
                      <div key={ci} style={{ position: 'relative' }}>
                        {/* Bubble */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0', borderRadius: '20px', overflow: 'hidden',
                          border: `1.5px solid ${anySelected ? '#FFD700' : '#cbd5e0'}`,
                          backgroundColor: anySelected ? 'rgba(255,215,0,0.08)' : '#f7fafc',
                        }}>
                          <button type="button" onClick={() => handleBubbleClick(cat)} style={{
                            padding: '0.45rem 0.85rem', background: 'none', border: 'none',
                            fontSize: '0.88rem', fontWeight: '600', cursor: 'pointer',
                            color: anySelected ? '#0d1f17' : '#4a5568',
                          }}>
                            {cat.label}
                          </button>
                          {anySelected && (
                            <button type="button" onClick={() => handleBubbleClick(cat)} style={{
                              padding: '0 0.5rem 0 0', background: 'none', border: 'none',
                              cursor: 'pointer', color: '#718096', fontSize: '1rem', lineHeight: 1,
                            }}>×</button>
                          )}
                          <button type="button" onMouseDown={e => e.stopPropagation()} onClick={() => setExpandedBubble(p => p === ci ? null : ci)} style={{
                            padding: '0.45rem 0.7rem 0.45rem 0', background: 'none', border: 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center',
                          }}>
                            <svg width="10" height="6" viewBox="0 0 10 6" fill="none"
                              style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                              <path d="M1 1l4 4 4-4" stroke={anySelected ? '#0d1f17' : '#718096'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                        {/* Subcategory dropdown */}
                        {isOpen && (
                          <div onMouseDown={e => e.stopPropagation()} style={{
                            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100,
                            backgroundColor: 'white', borderRadius: '10px', minWidth: '260px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0',
                            padding: '0.5rem 0',
                          }}>
                            {cat.subs.map(sub => {
                              const checked = formData.practiceAreas.includes(sub);
                              return (
                                <label key={sub} onClick={() => handlePracticeAreaToggle(sub)} style={{
                                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                                  padding: '0.45rem 1rem', cursor: 'pointer', fontSize: '0.88rem', color: '#2d3748',
                                }}>
                                  <span style={{
                                    width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0,
                                    border: `1.5px solid ${checked ? '#FFD700' : '#cbd5e0'}`,
                                    backgroundColor: checked ? '#FFD700' : 'white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>
                                    {checked && <svg width="9" height="7" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#0d1f17" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                  </span>
                                  {sub}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('register.bio')}</label>
                <textarea name="bio" style={styles.textarea} value={formData.bio} onChange={handleChange} />
              </div>
            </>
          )}

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? t('register.creatingAccount') : t('auth.registerButton')}
          </button>
        </form>

        {GOOGLE_ENABLED && (
          <>
            <div style={styles.divider}>
              <span style={styles.dividerLine}></span>
              <span>{t('auth.orContinueWith')}</span>
              <span style={styles.dividerLine}></span>
            </div>
            <div style={styles.googleContainer}>
              <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setError(t('errors.googleLoginFailed'))} />
            </div>
          </>
        )}

        <p style={styles.link}>
          {t('auth.haveAccount')}{' '}
          <Link to="/login" style={styles.linkAnchor}>{t('auth.signIn')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
